import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Crown, CheckCircle2, Clock, XCircle, Zap, ShieldCheck } from "lucide-react";
import { api, apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { AuthImage } from "../components/AuthImage";
import { useAuth } from "../context/AuthContext";

export default function Member() {
  const { refresh } = useAuth();
  const [m, setM] = useState(null);
  const [plans, setPlans] = useState([]);
  const [bank, setBank] = useState({ bank_name: "", bank_account: "", bank_owner: "" });
  const [sel, setSel] = useState("");
  const [proof, setProof] = useState("");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/membership/me").then((r) => setM(r.data));
  useEffect(() => {
    load();
    api.get("/membership/plans").then((r) => { setPlans(r.data.plans); setSel(r.data.plans[0]?.key || ""); });
    api.get("/settings/bank").then((r) => setBank(r.data));
  }, []);

  const onFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try { const { data } = await api.post("/uploads", fd); setProof(data.path); toast.success("Bukti transfer terunggah"); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!sel) { toast.error("Pilih paket dulu"); return; }
    if (!proof) { toast.error("Unggah bukti transfer dulu"); return; }
    setSaving(true);
    try {
      await api.post("/membership/apply", { payment_proof: proof, plan_key: sel, note });
      toast.success("Pengajuan terkirim! Menunggu verifikasi admin.");
      await refresh();
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!m) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-emerald-600" size={30} /></div>;
  const fmt = (d) => d ? new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) : "-";

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="text-center mb-6">
        <Crown className="mx-auto text-amber-500" size={36} />
        <h1 className="font-display text-3xl font-bold text-stone-900 mt-2">Keanggotaan Member</h1>
        <p className="text-stone-500 text-sm mt-1">Member bisa lihat <b>harga lengkap</b> & <b>mengajukan produk</b> untuk dicek admin ke supplier.</p>
      </div>

      {m.is_member ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center" data-testid="member-active">
          <CheckCircle2 className="mx-auto text-emerald-600" size={40} />
          <p className="font-display font-bold text-lg text-emerald-800 mt-2">Member Aktif ({m.plan === "platinum" ? "Platinum" : "Biasa"}) ✅</p>
          <p className="text-sm text-emerald-700 mt-1">Berlaku sampai <b>{fmt(m.expires_at)}</b> · maksimal <b>{m.daily_quota} produk/hari</b>.</p>
        </div>
      ) : m.status === "pending" ? (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center" data-testid="member-pending">
          <Clock className="mx-auto text-amber-600" size={40} />
          <p className="font-display font-bold text-lg text-amber-800 mt-2">Menunggu Verifikasi Admin</p>
          <p className="text-sm text-amber-700 mt-1">Bukti transfermu sedang dicek. Akses terbuka otomatis setelah disetujui.</p>
          {m.payment_proof && <AuthImage path={m.payment_proof} className="mx-auto mt-4 w-40 rounded-lg border border-amber-200" />}
        </div>
      ) : (
        <div className="space-y-5">
          {m.status === "rejected" && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-sm text-red-700" data-testid="member-rejected">
              <XCircle size={18} /> Pengajuan sebelumnya ditolak. Silakan ajukan ulang.
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            {plans.map((p) => {
              const platinum = p.plan === "platinum";
              const active = sel === p.key;
              return (
                <button key={p.key} data-testid={`member-plan-${p.key}`} onClick={() => setSel(p.key)}
                  className={`text-left rounded-2xl border-2 p-4 transition ${active ? (platinum ? "border-amber-500 bg-amber-50" : "border-emerald-600 bg-emerald-50") : "border-stone-200 bg-white hover:border-stone-300"}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    {platinum ? <Zap size={16} className="text-amber-500" /> : <ShieldCheck size={16} className="text-emerald-600" />}
                    <span className="font-display font-bold text-stone-900">{p.name}</span>
                  </div>
                  <p className="text-2xl font-display font-extrabold text-stone-900">{rupiah(p.price)}</p>
                  <p className="text-xs text-stone-500 mt-1">{p.days} hari · maksimal <b>{p.quota} produk/hari</b></p>
                </button>
              );
            })}
          </div>

          <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-xs text-stone-600 leading-relaxed">
            <b>Kenapa ada batas harian?</b> Untuk mencegah spam ke supplier. Member biasa hanya ~Rp1.000/hari (paket bulanan Rp30.000) dan boleh mengajukan <b>1 produk/hari</b>. Kalau butuh lebih, ambil <b>Platinum</b> yang boleh <b>10 produk/hari</b>.
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl p-5">
            <p className="font-semibold text-stone-800 mb-2">1. Transfer sesuai paket ke:</p>
            <div className="bg-stone-50 rounded-xl p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-stone-500">Bank</span><span className="font-semibold" data-testid="member-bank-name">{bank.bank_name}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">No. Rekening</span><span className="font-mono font-semibold" data-testid="member-bank-account">{bank.bank_account}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Atas Nama</span><span className="font-semibold" data-testid="member-bank-owner">{bank.bank_owner}</span></div>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3">
            <p className="font-semibold text-stone-800">2. Kirim bukti transfer</p>
            <label className="flex items-center gap-2 border border-dashed border-stone-300 rounded-lg px-3 py-2.5 text-sm text-stone-500 cursor-pointer hover:border-emerald-400">
              {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
              {proof ? "Ganti bukti transfer" : "Unggah foto bukti transfer"}
              <input data-testid="member-proof" type="file" accept="image/*" onChange={onFile} className="hidden" />
            </label>
            {proof && <AuthImage path={proof} className="w-40 rounded-lg border border-stone-200" />}
            <textarea data-testid="member-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Catatan (opsional): nama pengirim / jam transfer" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400 resize-none" />
            <button data-testid="member-submit" onClick={submit} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="animate-spin" size={16} />}Kirim Pengajuan Member
            </button>
            <p className="text-xs text-stone-400 text-center">Admin akan cek & setujui jika uang sudah diterima.</p>
          </div>
        </div>
      )}
    </div>
  );
}
